'use client'
import * as React from 'react'
import { useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'
import { sendAmplitudeEvent, AmplitudeEvents } from '@repo/analytics/amplitude/utils'
import { usePathname } from 'next/navigation'
import { AppRoutes } from '@repo/web-utils/app-routes'

import { useMatchMedia } from './use-match-media'
import { LFS_URL } from '@repo/web-utils/env'

function ToolsCard({ className }: { className?: string }) {
  const pathname = usePathname()
  const tool = useRandomTool()
  const showImage = useMatchMedia('(min-width: 1500px)')

  return (
    <div
      className={tw(
        'rounded-b-2 text-14 animate-fade-in col-span-2 flex h-full flex-wrap justify-between gap-0.5 bg-[#D9E2F7] bg-right bg-no-repeat p-2 transition-opacity duration-300',
        'min-[575px]:rounded-2 min-[575px]:text-16 min-[575px]:flex-nowrap min-[575px]:gap-2',
        'min-[1120px]:col-span-3',
        'min[1500px]:pr-32 min-[1500px]:col-span-2 min-[1500px]:flex-col',
        className,
      )}
      style={showImage ? { backgroundImage: `url(${tool.image})` } : undefined}
    >
      <div className="flex-shrink-0 flex-grow self-center min-[1500px]:self-auto min-[1500px]:pr-36">
        <div className={tw('text-14', 'min-[1120px]:text-16 font-semibold')}>{tool.name}</div>
        <div className="text-14 line-clamp-2 hidden font-normal min-[1120px]:block">
          {tool.description}
        </div>
      </div>
      <a
        href={tool.link}
        onClick={() => {
          sendAmplitudeEvent(AmplitudeEvents.RECOMMENDATION_CLICKED, {
            card_clicked: 'tool',
            card_url: tool.link,
            url: window.location.href,
            path: pathname,
          })
        }}
        target="_blank"
        className={tw(
          'text-14 text-cf-link rounded-2 h-fit w-fit flex-shrink-0 flex-grow-0 self-center',
          'min-[1120px]:text-16 min-[1120px]:text-cf-white min-[1120px]:bg-cf-violet min-[1120px]:hover:bg-cf-violet-hover min-[1120px]:rounded-2 min-[1120px]:p-2 min-[1120px]:px-6 min-[1500px]:self-auto',
        )}
        rel="noreferrer"
      >
        {tool.button}
      </a>
    </div>
  )
}

export const ToolsCardMemo = React.memo(ToolsCard)

interface Tool {
  id: string
  name: string
  description: string
  button: string
  image: string
  link: string
}

function useTools() {
  const t = useTranslations('prod.product_description_page.suggestions')
  const tools: Tool[] = [
    {
      id: 'background-remover',
      name: t('features.bg_remover.title'),
      description: t('features.bg_remover.description'),
      button: t('features.bg_remover.explore_tool'),
      image: `${LFS_URL}/web/pages/content/pdp/tools/bg_remover.png`,
      link: AppRoutes.STUDIO_TOOL_BACKGROUND_REMOVER,
    },
    {
      id: 'studio',
      name: t('features.studio.title'),
      description: t('features.studio.description'),
      button: t('features.studio.explore_tool'),
      image: `${LFS_URL}/web/pages/content/pdp/tools/studio.png`,
      link: AppRoutes.STUDIO_TOOL,
    },
    {
      id: 'ai-generator',
      name: t('features.ai_generator.title'),
      description: t('features.ai_generator.description'),
      button: t('features.ai_generator.explore_tool'),
      image: `${LFS_URL}/web/pages/content/pdp/tools/bg_remover.png`,
      link: AppRoutes.STUDIO_TOOL_FLOW,
    },
    {
      id: 'transfer',
      name: t('features.transfer.title'),
      description: t('features.transfer.description'),
      button: t('features.transfer.explore_tool'),
      image: `${LFS_URL}/web/pages/content/pdp/tools/transfer.png`,
      link: AppRoutes.STUDIO_TOOL_TRANSFER,
    },
    {
      id: 'vectorizer',
      name: t('features.vectorizer.title'),
      description: t('features.vectorizer.description'),
      button: t('features.vectorizer.explore_tool'),
      image: `${LFS_URL}/web/pages/content/pdp/tools/verctorizer.png`,
      link: AppRoutes.STUDIO_TOOL_VECTORIZER,
    },
  ]

  return tools
}

function useRandomTool() {
  const tools = useTools()
  const tool = React.useMemo(() => {
    return getRandomElement<Tool>(tools)
  }, [tools])

  return tool
}

function getRandomElement<T extends object>(arr: T[]) {
  return arr[Math.floor(Math.random() * arr.length)]
}
